import i18n, { getFallbackLocale } from "@/i18n";

/**
 * This function formats a timestamp into a date string based on the provided key and locale.
 *
 * @param {number} timestamp - The timestamp to format (in seconds or milliseconds).
 * @param {string} key - The format key for the date (e.g., "narrow", "short", "medium", "long").
 * @param {string} [locale] - Optional locale to use for formatting. If not provided, it will use the current locale or fallback.
 * @returns {string} The formatted date string.
 */
export function formatDate(
  timestamp: number,
  key: "narrow" | "short" | "medium" | "long" = "long",
  locale: string | undefined = undefined
): string {
  if (timestamp < 1000000000000) timestamp *= 1000; // Convert to milliseconds if needed
  const date = new Date(timestamp);
  // Use the date formatting option directly
  return i18n.global.d(date, { key }, locale || getFallbackLocale(i18n.global.locale.value));
}

/**
 * Formats a validThrough timestamp into separate date and hour strings for
 * the "pay before" / "overdue on" subtitle display.
 *
 * For Portuguese and Indonesian locales, the locale's own narrow format is
 * used for the hour (e.g., "23h59", "20.00"). All other locales fall back to
 * "en-US" which renders the hour as "8 PM".
 *
 * @param {number} timestamp - The validThrough timestamp (seconds or ms).
 * @returns {string} Formatted date and hour string.
 */
export function formatPayBeforeDateTime(timestamp: number): string {
  const locale = i18n.global.locale.value;
  const customLocale = ["pt", "id"].some((lang) => locale.includes(lang)) ? locale : "en-US";

  let date = formatDate(timestamp, "medium");
  let hour = formatDate(timestamp, "narrow", customLocale);

  if (locale.includes("es")) {
    // Add a comma after the weekday
    date = `${date.slice(0, 3)},${date.slice(3)}`;
    // Remove the "de" between the day and month
    date = date.replace(" de ", " ");
  } else if (locale.includes("pt")) {
    // Remove dots from the date
    date = date.replace(/\./g, "");
    // Replace the ":" with "h" in the hour
    hour = hour.replace(":", "h");
  }
  return i18n.global.t("pay-before-datetime", { date, hour });
}

/**
 * This function obtains the weekday names per locale, and returns it as a string array.
 *
 * @param {string} locale - Two-letter string representing the language (e.g. 'en' for English).
 * @param {string} weekFormat - Desired format of names (e.g. 'long' for 'Monday').
 * @returns {Array} Returns an array of strings representing the weekdays.
 */
export function getWeekdayNames(
    locale = 'en',
    weekFormat: 'short' | 'long' | 'narrow' = 'long'
): string[] {
  const formatter = new Intl.DateTimeFormat(locale, { weekday: weekFormat });
  const weekdays: string[] = [];

  // January 4, 1970 is a Sunday
  for (let day = 0; day < 7; day++) {
    const date = new Date(1970, 0, 4 + day);
    weekdays.push(formatter.format(date));
  }
  return weekdays;
}

/**
 * This function obtains the month names per locale, and returns it as a string array.
 *
 * @param {string} locale - Two-letter string representing the language (e.g. 'en' for English).
 * @param {string} monthFormat - Desired format of names (e.g. 'long' for 'January').
 * @returns {Array} Returns an array of strings representing the month names.
 */
export function getMonthNames(
    locale = 'en',
    monthFormat: 'short' | 'long' | 'narrow' = 'long'
): string[] {

  const formatter = new Intl.DateTimeFormat(locale, { month: monthFormat });
  const months: string[] = [];

  for (let month = 0; month < 12; month++) {
    const date = new Date(2000, month, 1);
    months.push(formatter.format(date));
  }
  return months;
}

/**
 * Returns the number of days in the given month.
 *
 * @param {Date} [date] - The date to use. Defaults to the current date.
 * @returns {number} The number of days in the month (28, 29, 30, or 31).
 */
export function getDaysInMonth(date: Date = new Date()): number {
  return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
}

/**
 * This function formats a date using the following format: Lun, 9 de sep, 14h27.
 *
 * @param {string|number|Date} date - A valid date to format.
 * @param {string} locale - Two-letter string representing the language (e.g. 'en' for English).
 * @param {boolean} keepDotInNames - If true, removes the last '.' from the weekday and month name.
 * @returns {string} Returns the formatted date as a string.
 */
export function formatDateWeekday(date: string|number|Date, locale: string, keepDotInNames = false): string {

  if(locale.includes('-')) {
    // if the locale comes with the country code, extract the language
    // e.g. 'pt-BR' => 'pt'
    locale = locale.split('-')[0];
  }

  if(!(date instanceof Date)) {
    // ensure the date parameter is a date object, starting from this point
    date = new Date(date);
  }

  const weekdays = getWeekdayNames(locale, 'short');
  const months = getMonthNames(locale, 'short');

  let weekday = weekdays[date.getDay()];
  const day = date.getDate();
  let month = months[date.getMonth()];

  if(!keepDotInNames) {
    // if the weekday and month end with '.', remove it
    weekday = weekday.replace(/\.$/, '');
    month = month.replace(/\.$/, '');
  }

  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');

  return `${weekday}, ${day} de ${month}, ${hours}h${minutes}`;
}

/**
 * Like formatDateWeekday but uses 12-hour AM/PM time — intended for Peru (es-PE).
 * Output example: "lun 20 de abr, 2:56 p. m."
 */
export function formatDateWeekday12h(date: string|number|Date, locale: string): string {
  if(!(date instanceof Date)) {
    date = new Date(date);
  }

  const langLocale = locale.includes('-') ? locale.split('-')[0] : locale;
  const weekdays = getWeekdayNames(langLocale, 'short');
  const months = getMonthNames(langLocale, 'short');

  const weekday = weekdays[date.getDay()].replace(/\.$/, '');
  const day = date.getDate();
  const month = months[date.getMonth()].replace(/\.$/, '');

  const timeStr = new Intl.DateTimeFormat(locale, {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).format(date);

  return `${weekday} ${day} de ${month}, ${timeStr}`;
}
